import styled from "@emotion/styled/macro";
import { tokens, Typography } from "@sunrun/experience-ui-components";
import { useEffect, useMemo } from "react";
import { TaskCheckMark } from "../atoms/TaskCheckMark";
import { CheckoutTask } from "checkout/api/checkoutSteps";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useAppSelector } from "store";
import { useContactsData } from "utils/swrHooks";

type TaskItemProps = CheckoutTask & {
  isSelected?: boolean;
  onClick?: () => void;
  id: string;
  className?: string;
  isMobile?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isCustomer?: boolean;
  hasWarning?: boolean;
};

const TaskItem = ({
  label,
  isUnlocked,
  isComplete,
  isInProgress,
  isSelected = false,
  onClick,
  urlSlug,
  id: taskId,
  className,
  isMobile,
  isFirst,
  isLast,
  isCustomer,
  hasWarning,
}: TaskItemProps) => {
  const { titleOverride, prospectId, handleTitleOverride, offer } =
    useCheckoutContext();
  const locked = !isUnlocked;
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const { contacts } = useContactsData(prospectId as string, authKey);

  useEffect(() => {
    if (!contacts || taskId !== "deposit" || !offer) {
      return;
    }
    const isMonthly = offer.financial.financePlan
      ?.toLowerCase()
      .includes("monthly");
    if (isMonthly) {
      handleTitleOverride("Upfront Payment", "deposit");
    }
  }, [contacts, handleTitleOverride, taskId, offer]);

  const ariaLabelStatus = useMemo(() => {
    if (isSelected) return "selected";
    if (isComplete) return "completed";
    if (isInProgress) return "in progress";
    if (locked) return "locked";
    return "incomplete";
  }, [isComplete, isInProgress, isSelected, locked]);

  const titleOverrideString = titleOverride ? titleOverride[taskId] : undefined;
  const title = titleOverrideString || label;

  return (
    <Button
      isMobile={isMobile}
      className={className}
      $selected={isSelected}
      disabled={locked}
      onClick={onClick}
      data-testid={`checkout-task-list-${urlSlug}`}
      aria-label={`${label} is ${ariaLabelStatus}`}
    >
      {isMobile ? (
        <MobileIconWrapper>
          <Line hide={isFirst} isFewerThanFourTasks={isCustomer} />
          <TaskCheckMark
            checked={isComplete}
            locked={locked}
            halfChecked={isInProgress}
            hasWarning={hasWarning}
          />
          <Line hide={isLast} isFewerThanFourTasks={isCustomer} />
        </MobileIconWrapper>
      ) : (
        <IconWrapper>
          <TaskCheckMark
            checked={isComplete}
            locked={locked}
            halfChecked={isInProgress}
            hasWarning={hasWarning}
          />
        </IconWrapper>
      )}
      <Typography
        variant="p"
        color={locked ? tokens.TINTS_OFF_WHITE_30 : tokens.BLACK}
      >
        {title}
      </Typography>
    </Button>
  );
};

const Button = styled.button<{ $selected: boolean; isMobile?: boolean }>`
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  padding: ${(props) => (props.isMobile ? "0px" : "8px 16px")};
  gap: ${(props) => (props.isMobile ? "4px" : "16px")};
  background-color: transparent;
  border-radius: 8px;
  border: none;

  ${(props) =>
    props.$selected &&
    `
    background-color: ${
      props.isMobile ? "transparent" : tokens.TINTS_HERO_BLUE_90
    }; 
    > p {
      font-weight: ${props.isMobile ? "600" : "400"};
    }
    `}
  &:hover {
    background-color: ${(props) =>
      props.isMobile ? "transparent" : tokens.INFO_90};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const MobileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Line = styled.div<{ hide?: boolean; isFewerThanFourTasks?: boolean }>`
  height: 1px;
  width: ${(props) => (props.isFewerThanFourTasks ? "48px" : "30px")};
  background-color: ${(props) =>
    props.hide ? "transparent" : tokens.TINTS_OFF_WHITE_30};
`;

export { TaskItem };
export type { TaskItemProps };
